import type { ApiResponse } from '@/types/api.types'
import type {
  ChangePasswordRequest,
  LoginCredentials,
  LoginResponse,
  LogoutAllResponse,
  RefreshTokenRequest,
  RefreshTokenResponse
} from '@/types/auth.types'
import { tokenManager } from '@/utils/tokenManager'

import { apiClient, ensureValidToken } from '../client'

export class AuthService {
  async login(email: string, password: string): Promise<LoginResponse> {
    const credentials: LoginCredentials = { email, password }
    const response = await apiClient.post<ApiResponse<LoginResponse>>(
      '/api/v1/auth/login',
      credentials
    )

    if (response.data && response.data.data) {
      const loginData = response.data.data as LoginResponse

      tokenManager.setTokens({
        token: loginData.token,
        refresh_token: loginData.refresh_token
      })

      return loginData
    }

    throw new Error('Invalid login response')
  }

  async logout(): Promise<void> {
    const refreshToken = tokenManager.getRefreshToken()

    try {
      if (refreshToken) {
        const logoutData: RefreshTokenRequest = { refresh_token: refreshToken }
        await apiClient.post('/api/v1/auth/logout', logoutData)
      }
    } catch (error) {
      console.error('Logout API call failed:', error)
    } finally {
      tokenManager.clearTokens()
    }
  }

  async logoutSession(sessionId: string): Promise<void> {
    await ensureValidToken()
    await apiClient.post(`/auth/logout-session/${sessionId}`)
  }

  async refreshToken(): Promise<RefreshTokenResponse> {
    const refreshToken = tokenManager.getRefreshToken()

    if (!refreshToken) {
      throw new Error('No refresh token available')
    }

    const refreshData: RefreshTokenRequest = { refresh_token: refreshToken }
    const response = await apiClient.post<ApiResponse<RefreshTokenResponse>>(
      '/api/v1/auth/refresh',
      refreshData
    )

    if (response.data && response.data.data) {
      const tokens = response.data.data

      tokenManager.setTokens({
        token: tokens.token,
        refresh_token: tokens.refresh_token
      })

      return tokens
    }

    throw new Error('Invalid refresh token response')
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await ensureValidToken()

    const passwordData: ChangePasswordRequest = {
      current_password: currentPassword,
      new_password: newPassword
    }

    await apiClient.post('/api/v1/auth/change-password', passwordData)
  }

  async logoutAllSessions(): Promise<LogoutAllResponse> {
    await ensureValidToken()
    const response = await apiClient.post<ApiResponse<LogoutAllResponse>>('/auth/logout-all')

    if (response.data && response.data.data) {
      return response.data.data
    }

    throw new Error('Failed to logout all sessions')
  }

  isAuthenticated(): boolean {
    return tokenManager.isAuthenticated()
  }

  clearSession(): void {
    tokenManager.clearTokens()
  }
}

export const authService = new AuthService()
